import React, { useState } from "react";
import {
  Button,
  createStyles,
  Divider,
  Select,
  TextInput,
  Modal,
  Box,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useSelector } from "react-redux";
import { LuChevronDown } from "react-icons/lu";
import swal from "sweetalert";
import { BillingLookups } from "../../services";

const useStyles = createStyles(() => ({
  modalBody: {
    padding: "22px 28px !important",
  },
  headerSubtitle: {
    fontSize: "13px",
    color: "var(--app-text-muted)",
    lineHeight: 1.5,
    margin: "4px 0 0",
  },
  formRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
  },
  flex1: { flex: 1, minWidth: "140px" },
  flex25: { flex: 2.5, minWidth: "240px" },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "12.5px",
    fontWeight: 600,
    color: "var(--app-text)",
    lineHeight: 1.4,
  },
  req: {
    color: "#DC2626",
  },
  selectBox: {
    "& .mantine-Select-wrapper": { position: "relative" },
    "& input": {
      height: "42px",
      borderRadius: "8px",
      border: "1px solid var(--app-border)",
      fontSize: "13px",
      color: "var(--app-text)",
      backgroundColor: "var(--app-surface)",
      paddingLeft: "12px",
      paddingRight: "36px",
      fontFamily: "inherit",
      outline: "none",
      transition: "border-color .12s ease, box-shadow .12s ease",
      "&:focus": {
        borderColor: "var(--app-text)",
        boxShadow: "0 0 0 3px rgba(23,23,23,0.08)",
      },
    },
    "& .mantine-Select-rightSection": {
      pointerEvents: "none",
      color: "var(--app-text)",
    },
  },
  readonlyInput: {
    "& input": {
      height: "42px",
      borderRadius: "8px",
      border: "1px solid var(--app-border)",
      fontSize: "13px",
      color: "var(--app-text)",
      backgroundColor: "var(--app-surface-2)",
      paddingLeft: "12px",
      cursor: "default",
      fontFamily: "inherit",
    },
  },
  findRateBtn: {
    height: "44px",
    borderRadius: "8px",
    fontSize: "13.5px",
    fontWeight: 600,
    backgroundColor: "var(--app-primary)",
    color: "var(--app-on-primary)",
    width: "100%",
    marginTop: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": { backgroundColor: "var(--app-primary)" },
  },
  divider: {
    margin: "22px 0",
    borderColor: "var(--app-border)",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "10px",
    padding: "16px 28px 22px",
    borderTop: "1px solid var(--app-border)",
    margin: "22px -28px -22px",
  },
  cancelBtn: {
    height: "40px",
    borderRadius: "8px",
    fontSize: "13.5px",
    fontWeight: 600,
    border: "1px solid var(--app-border)",
    color: "var(--app-text)",
    backgroundColor: "transparent",
    paddingLeft: "16px",
    paddingRight: "16px",
    "&:hover": { backgroundColor: "var(--app-surface-2)" },
  },
  addBtn: {
    height: "40px",
    borderRadius: "8px",
    fontSize: "13.5px",
    fontWeight: 600,
    backgroundColor: "var(--app-primary)",
    color: "var(--app-on-primary)",
    paddingLeft: "16px",
    paddingRight: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": { backgroundColor: "var(--app-primary)" },
    "&:disabled": { opacity: 0.45, cursor: "not-allowed" },
  },
}));

const STATE_OPTIONS = [
  { value: "Arizona", label: "Arizona" },
  { value: "Texas", label: "Texas" },
  { value: "California", label: "California" },
  { value: "Florida", label: "Florida" },
  { value: "New York", label: "New York" },
];

const REGION_OPTIONS = [
  { value: "FFS", label: "FFS" },
  { value: "MCO", label: "MCO" },
  { value: "Flagstaff", label: "Flagstaff" },
  { value: "Phoenix", label: "Phoenix" },
  { value: "Tucson", label: "Tucson" },
];

const RATE_BOOK_OPTIONS = [
  {
    value: "Community",
    label: "FFS Home and Community Based Services (HCBS) Rates",
  },
  {
    value: "health",
    label: "Arizona – FFS & MCO Behavioral Health Outpatient Rates",
  },
  { value: "inpatient", label: "Arizona – FFS Inpatient Hospital Rates" },
  { value: "physician", label: "Arizona – Medicaid Physician Fee Schedule" },
  { value: "texas", label: "Texas Medicaid Provider Fee Schedule" },
];

const ChevronIcon = () => <LuChevronDown size={16} color="var(--app-text)" />;

const ProviderServicesLookupModal = ({
  openlookup,
  setOpenLookup,
  setProviderServices,
}) => {
  const { classes } = useStyles();
  const [selectedServiceObject, setSelectedServiceObject] = useState(null);
  const [loading, setLoading] = useState(false);

  const { billinglookup } = useSelector((state) => state.billing);

  const form = useForm({
    initialValues: { state: "", region: "", rateBook: "" },
  });

  const handleClose = () => {
    setOpenLookup(false);
    setSelectedServiceObject(null);
    form.reset();
  };

  const onFindRate = async () => {
    const { state, region, rateBook } = form.values;
    const missing = [];
    if (!state) missing.push("State");
    if (!region) missing.push("Region");
    if (!rateBook) missing.push("Rate Book");

    if (missing.length > 0) {
      await swal({
        title: "Please fill out the following fields",
        text: missing.join(", "),
        icon: "warning",
        dangerMode: true,
      });
      return;
    }

    try {
      setLoading(true);
      await BillingLookups(form.values);
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = () => {
    if (selectedServiceObject && setProviderServices) {
      setProviderServices({
        servicetitle: selectedServiceObject.serviceDescription || "",
        servicenumber: selectedServiceObject.serviceCode || "",
        servicechargetype: "",
        servicechargeNumber: selectedServiceObject.rate || "",
      });
    }
    handleClose();
  };

  const serviceOptions = Array.from(
    new Set(
      billinglookup?.billinglookup?.map((obj) => obj.serviceDescription) ?? []
    )
  )
    .sort()
    .map((desc) => ({ value: desc, label: desc }));

  return (
    <Modal
      opened={openlookup}
      size={640}
      onClose={handleClose}
      overlayProps={{ blur: 3 }}
      radius="lg"
      zIndex={1000}
      centered
      title={
        <Box>
          <Text fw={700} fz={22} color="var(--app-text)" ff="Inter" lh={1.2}>
            Lookup Rates
          </Text>
          <Text className={classes.headerSubtitle}>
            Search published payer rate books by state, region, and book. Pick a
            service from the results to copy its code and rate.
          </Text>
        </Box>
      }
      classNames={{ body: classes.modalBody }}
      styles={{
        header: {
          padding: "22px 28px",
          borderBottom: "1px solid var(--app-border)",
          alignItems: "flex-start",
          gap: "16px",
        },
        close: { color: "var(--app-text)" },
      }}
    >
      {/* Row 1: State / Region / Rate Book */}
      <Box className={classes.formRow}>
        <Box className={`${classes.flex1} ${classes.field}`}>
          <Text className={classes.label}>
            Select State{" "}
            <Text component="span" className={classes.req}>*</Text>
          </Text>
          <Select
            className={classes.selectBox}
            placeholder="State"
            data={STATE_OPTIONS}
            rightSection={<ChevronIcon />}
            {...form.getInputProps("state")}
          />
        </Box>
        <Box className={`${classes.flex1} ${classes.field}`}>
          <Text className={classes.label}>
            Select Region{" "}
            <Text component="span" className={classes.req}>*</Text>
          </Text>
          <Select
            className={classes.selectBox}
            placeholder="Region"
            data={REGION_OPTIONS}
            rightSection={<ChevronIcon />}
            {...form.getInputProps("region")}
          />
        </Box>
        <Box className={`${classes.flex25} ${classes.field}`}>
          <Text className={classes.label}>
            Select Rate Book{" "}
            <Text component="span" className={classes.req}>*</Text>
          </Text>
          <Select
            className={classes.selectBox}
            placeholder="Rate Book"
            data={RATE_BOOK_OPTIONS}
            rightSection={<ChevronIcon />}
            {...form.getInputProps("rateBook")}
          />
        </Box>
      </Box>

      <Button
        className={classes.findRateBtn}
        onClick={onFindRate}
        loading={loading}
      >
        Find Rate
      </Button>

      <Divider className={classes.divider} />

      {/* Row 2: Service Title / Service Code / Rate Amount */}
      <Box className={classes.formRow}>
        <Box className={`${classes.flex25} ${classes.field}`}>
          <Text className={classes.label}>
            Service Title{" "}
            <Text component="span" className={classes.req}>*</Text>
          </Text>
          <Select
            className={classes.selectBox}
            placeholder="Select a service"
            data={serviceOptions}
            searchable
            rightSection={<ChevronIcon />}
            onChange={(value) => {
              const matched = billinglookup?.billinglookup?.find(
                (obj) => obj.serviceDescription === value
              );
              setSelectedServiceObject(matched ?? null);
            }}
          />
        </Box>
        <Box className={`${classes.flex1} ${classes.field}`}>
          <Text className={classes.label}>Service Code</Text>
          <TextInput
            className={classes.readonlyInput}
            placeholder="—"
            value={selectedServiceObject?.serviceCode ?? ""}
            readOnly
          />
        </Box>
        <Box className={`${classes.flex1} ${classes.field}`}>
          <Text className={classes.label}>Rate Amount</Text>
          <TextInput
            className={classes.readonlyInput}
            placeholder="—"
            value={selectedServiceObject?.rate ?? ""}
            readOnly
          />
        </Box>
      </Box>

      {/* Footer — matches .era-modal-foot exactly */}
      <Box className={classes.footer}>
        <Button
          className={classes.cancelBtn}
          variant="default"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          className={classes.addBtn}
          onClick={handleAddService}
          disabled={!selectedServiceObject}
        >
          + Add to client services
        </Button>
      </Box>
    </Modal>
  );
};

export default ProviderServicesLookupModal;
